import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Clock, LogIn, LogOut, MapPin, Camera, Wifi, TimerReset, ChevronLeft, ChevronRight
} from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}
function secondsToHM(s) {
  if (!s) return "0h 0m";
  const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
}

export default function Attendance() {
  const { user } = useAuth();
  const [now, setNow] = useState(new Date());
  const [today, setToday] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [monthData, setMonthData] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const loadToday = () => api.get("/attendance/today").then(r => setToday(r.data)).catch(() => {});
  const loadMonth = () => {
    const y = month.getFullYear(); const m = month.getMonth() + 1;
    api.get(`/attendance/month?year=${y}&month=${m}`).then(r => setMonthData(r.data)).catch(() => {});
  };
  useEffect(() => { loadToday(); }, []);
  useEffect(() => { loadMonth(); }, [month]);

  const doAction = async (path, label) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(label);
      loadToday(); loadMonth();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Live working hours
  const liveWorked = useMemo(() => {
    if (!today?.check_in) return 0;
    const start = new Date(today.check_in);
    const end = today.check_out ? new Date(today.check_out) : now;
    return Math.max(0, Math.floor((end - start) / 1000));
  }, [today, now]);

  // Calendar grid
  const cal = useMemo(() => {
    const y = month.getFullYear(); const m = month.getMonth();
    const firstDay = new Date(y, m, 1).getDay();
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const byDate = Object.fromEntries(monthData.map(r => [r.date, r]));
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, iso, rec: byDate[iso] });
    }
    return cells;
  }, [month, monthData]);

  const statusColor = {
    Present: "bg-emerald-500", Late: "bg-orange-500", Absent: "bg-red-500",
    "Half Day": "bg-yellow-500", Leave: "bg-red-500",
  };

  return (
    <div className="space-y-6" data-testid="attendance-root">
      <div>
        <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Attendance</div>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Clock in with confidence</h1>
        <p className="mt-1 text-sm text-slate-500">Live time, working hours, and a full month of your check-ins.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Live clock card */}
        <Card className="lg:col-span-2 p-6 rounded-xl border-slate-200/60 relative overflow-hidden">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-red-100/40 blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 text-xs text-slate-500 uppercase font-semibold tracking-[0.1em]">
              <span className="h-2 w-2 rounded-full bg-emerald-500 pulse-dot" />
              LIVE — {now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}
            </div>
            <div className="mt-3 font-display text-6xl sm:text-7xl font-extrabold tracking-tight text-slate-900 tabular-nums" data-testid="live-clock">
              {now.toLocaleTimeString("en-IN", { hour12: false })}
            </div>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Meta icon={LogIn} label="Check In" value={fmtTime(today?.check_in)} tone="emerald" />
              <Meta icon={LogOut} label="Check Out" value={fmtTime(today?.check_out)} tone="red" />
              <Meta icon={Clock} label="Working" value={secondsToHM(liveWorked)} tone="blue" />
              <Meta icon={TimerReset} label="Status" value={today?.status || "Not clocked in"} tone="slate" />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={() => doAction("/attendance/checkin", "Checked in")}
                disabled={busy || (today && today.check_in)}
                className="bg-[#DC2626] hover:bg-[#B91C1C] h-11 rounded-lg px-6"
                data-testid="check-in-btn"
              >
                <LogIn className="h-4 w-4 mr-2" strokeWidth={1.8} />
                Check In
              </Button>
              <Button
                onClick={() => doAction("/attendance/checkout", "Checked out")}
                disabled={busy || !today?.check_in || !!today?.check_out}
                variant="outline"
                className="h-11 rounded-lg px-6 border-slate-300"
                data-testid="check-out-btn"
              >
                <LogOut className="h-4 w-4 mr-2" strokeWidth={1.8} />
                Check Out
              </Button>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" strokeWidth={1.5} /> Bengaluru HQ · 12.97° N, 77.59° E</span>
              <span className="flex items-center gap-1.5"><Wifi className="h-3.5 w-3.5" strokeWidth={1.5} /> Office network</span>
              <span className="flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" strokeWidth={1.5} /> Selfie capture: available in Phase 2</span>
            </div>
          </div>
        </Card>

        {/* Today's summary */}
        <Card className="p-6 rounded-xl border-slate-200/60">
          <h3 className="font-display text-lg font-semibold text-slate-900">Today at a glance</h3>
          <p className="text-xs text-slate-500 mt-0.5">Hi, {user?.name?.split(" ")[0]}</p>
          <div className="mt-5 space-y-4">
            <Row label="Shift" value="09:00 – 18:00" />
            <Row label="Break allowed" value="60 min" />
            <Row label="Overtime today" value={liveWorked > 8 * 3600 ? secondsToHM(liveWorked - 8 * 3600) : "0h 0m"} />
            <Row label="Late threshold" value="10:00 AM" />
          </div>
        </Card>
      </div>

      {/* Monthly calendar */}
      <Card className="p-6 rounded-xl border-slate-200/60">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">Monthly view</h3>
            <p className="text-xs text-slate-500">Your attendance this month</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="cal-prev">
              <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <div className="text-sm font-semibold text-slate-900 w-32 text-center">
              {month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
            </div>
            <button onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="cal-next">
              <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-2 text-[11px] uppercase font-semibold tracking-wider text-slate-400 mb-2">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => <div key={d} className="text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {cal.map((c, i) => (
            <div key={c?.iso || `pad-${i}`} className={`aspect-square rounded-lg border ${c ? "border-slate-200 bg-white" : "border-transparent"} p-2 flex flex-col`}>
              {c && (
                <>
                  <div className="text-xs font-semibold text-slate-700">{c.day}</div>
                  {c.rec ? (
                    <div className="mt-auto space-y-1">
                      <div className={`h-1.5 rounded-full ${statusColor[c.rec.status] || "bg-slate-300"}`} />
                      <div className="text-[10px] text-slate-500 truncate">{fmtTime(c.rec.check_in)}</div>
                    </div>
                  ) : (
                    <div className="mt-auto text-[10px] text-slate-300">—</div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-xs text-slate-500">
          {Object.entries(statusColor).map(([k, cls]) => (
            <div key={k} className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${cls}`} /> {k}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Meta({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "text-emerald-600", red: "text-red-600", blue: "text-[#DC2626]", slate: "text-slate-700",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white/60 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-semibold tracking-wider text-slate-500">
        <Icon className="h-3 w-3" strokeWidth={1.8} /> {label}
      </div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-slate-100 pb-3 last:border-0 last:pb-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
